import { FC, useMemo, useCallback, useState } from 'react'
import { useLoadAllItemsQuery, useQuery } from '@sequencehq/api'
import {
  Text,
  Badge,
  Banner,
  EmptyState,
  GreyGrey80,
  IconReceiptTax,
  IndigoIndigo60,
  Link,
  Pill,
  Primary13Regular,
  Primary14SemiBold,
  SequenceKebabMenu,
  SequenceMenuItem,
  SequenceMenuItemProps,
  SettingsFormControl,
  SettingsFormControlIcon,
  SettingsGroup,
  Flex
} from '@sequencehq/core-components'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useMarkTaxCategoryAsDefault } from 'SettingsV2/components/tax-rates/hooks/useMarkTaxCategoryAsDefault'
import { SettingsPageLayout } from 'SettingsV2/components/SettingsPageLayout'
import { anrokIntegrationConfig } from 'Integrations/integrationsConfig/anrok/anrok.integration.config'
import { SmallIcon } from 'Integrations/index'
import { useCreateAnrokMapping } from './hooks/useCreateAnrokMapping'
import { useDeleteAnrokMapping } from './hooks/useDeleteAnrokMapping'
import { useUpdateAnrokMapping } from './hooks/useUpdateAnrokMapping'
import { useUpdateTaxCategory } from '../hooks/useUpdateTaxCategory'
import { useCreateTaxCategoryWithAnrokMapping } from './hooks/useCreateTaxCategoryWithAnrokMapping'
import AddTaxCategoryForAnrokModal from './modals/AddTaxCategoryForAnrokModal'
import EditTaxCategoryNameModal from './modals/EditTaxCategoryNameModal'
import EditAnrokMappingModal from './modals/EditAnrokMappingModal'
import AddAnrokMappingModal from './modals/AddAnrokMappingModal'
import { AnrokTaxCategoryMapping } from 'SettingsV2/components/tax-rates/taxRates.types'

type MappingRow = {
  taxCategoryId: string
  taxCategoryName: string
  isDefault: boolean
  isMappingDefault: boolean
  anrokProductExternalId: string | undefined
  anrokTaxCategoryId: string | undefined
  anrokTaxCategoryType: string | undefined
}

const TaxCategoryRow = ({
  mapping,
  onUpdateName,
  onUpdateMapping,
  onDeleteMapping,
  onMarkAsDefault,
  onAddMapping
}: {
  mapping: MappingRow
  onUpdateName: (taxCategoryId: string, name: string) => Promise<void>
  onUpdateMapping: (params: {
    taxCategoryId: string
    taxCategoryName: string
    anrokTaxCategoryId: string
    anrokTaxCategoryType: 'standard' | 'custom'
    isDefault: boolean
  }) => Promise<void>
  onDeleteMapping: (taxCategoryId: string) => void
  onMarkAsDefault: (taxCategoryId: string) => void
  onAddMapping: (values: {
    taxCategoryId: string
    taxCategoryName: string
    anrokTaxCategoryId: string
    anrokTaxCategoryType: 'standard' | 'custom'
    isDefault: boolean
  }) => Promise<void>
}) => {
  const [showEditNameModal, setShowEditNameModal] = useState(false)
  const [showEditMappingModal, setShowEditMappingModal] = useState(false)

  return (
    <>
      <SettingsFormControl
        label={mapping.taxCategoryName}
        htmlFor={`tax-category-${mapping.taxCategoryId}`}
        icon={
          <SettingsFormControlIcon>
            <IconReceiptTax />
          </SettingsFormControlIcon>
        }
      >
        <Flex alignItems="center" gap="8px" paddingLeft="16px">
          {mapping.isDefault ? (
            <Badge sentiment="neutral" size="sm">
              Default
            </Badge>
          ) : null}

          {mapping.anrokTaxCategoryId ? (
            <Pill
              data-testid={`anrokTaxCodeMappingTable.remove.${mapping.taxCategoryId}`}
              interactive
              onClear={() => onDeleteMapping(mapping.taxCategoryId)}
            >
              {mapping.anrokTaxCategoryId}
            </Pill>
          ) : (
            <AddAnrokMappingModal
              taxCategoryId={mapping.taxCategoryId}
              taxCategoryName={mapping.taxCategoryName}
              onAddMapping={onAddMapping}
            />
          )}

          <SequenceKebabMenu
            variant="ghost"
            buttonSize="small"
            data-testid={`kebab.${mapping.taxCategoryName}`}
            items={[
              (props: Partial<SequenceMenuItemProps>) => (
                <SequenceMenuItem
                  data-testid={`editName.${mapping.taxCategoryName}`}
                  key="edit-name"
                  uuid="edit-name"
                  label="Edit name"
                  onClick={() => {
                    props?.setIsOpen?.(false)
                    setShowEditNameModal(true)
                  }}
                />
              ),
              ...(mapping.anrokTaxCategoryId && mapping.anrokTaxCategoryType
                ? [
                    (props: Partial<SequenceMenuItemProps>) => (
                      <SequenceMenuItem
                        data-testid={`editMapping.${mapping.taxCategoryName}`}
                        key="edit-mapping"
                        uuid="edit-mapping"
                        label="Edit mapping"
                        onClick={() => {
                          props?.setIsOpen?.(false)
                          setShowEditMappingModal(true)
                        }}
                      />
                    )
                  ]
                : []),
              ...(!mapping.isDefault
                ? [
                    (props: Partial<SequenceMenuItemProps>) => (
                      <SequenceMenuItem
                        data-testid={`markAsDefault.${mapping.taxCategoryName}`}
                        key="mark-as-default"
                        uuid="mark-as-default"
                        label="Mark as default"
                        onClick={() => {
                          onMarkAsDefault(mapping.taxCategoryId)
                          return props?.setIsOpen?.(false)
                        }}
                      />
                    )
                  ]
                : [])
            ]}
          />
        </Flex>
      </SettingsFormControl>

      {showEditNameModal && (
        <EditTaxCategoryNameModal
          initialName={mapping.taxCategoryName}
          onSubmit={async ({ name }) => {
            await onUpdateName(mapping.taxCategoryId, name)
          }}
          onClose={() => setShowEditNameModal(false)}
        />
      )}

      {showEditMappingModal &&
        mapping.anrokTaxCategoryId &&
        mapping.anrokTaxCategoryType && (
          <EditAnrokMappingModal
            taxCategoryName={mapping.taxCategoryName}
            currentAnrokTaxCategoryId={mapping.anrokTaxCategoryId}
            currentAnrokTaxCategoryType={
              mapping.anrokTaxCategoryType as 'standard' | 'custom'
            }
            isDefault={mapping.isMappingDefault}
            onUpdateMapping={async ({
              anrokTaxCategoryId,
              anrokTaxCategoryType,
              isDefault
            }) => {
              await onUpdateMapping({
                taxCategoryId: mapping.taxCategoryId,
                taxCategoryName: mapping.taxCategoryName,
                anrokTaxCategoryId,
                anrokTaxCategoryType,
                isDefault
              })
            }}
            onClose={() => setShowEditMappingModal(false)}
          />
        )}
    </>
  )
}

export const AnrokSalesTaxSettings: FC = () => {
  const {
    data: taxCategoriesData,
    isError,
    isLoading
  } = useLoadAllItemsQuery(dashboardv99990101Client.getTaxCategories)

  const mappingsResult = useQuery(
    dashboardv99990101Client.getAllTaxCategoriesWithAnrokMappings,
    undefined,
    {
      retry: 1,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  )

  const allExistingTaxCategories = useMemo(
    () => taxCategoriesData?.pages.flatMap(page => page.items) ?? [],
    [taxCategoriesData?.pages]
  )

  const taxCategoriesWithAnrokMappings = useMemo(() => {
    return allExistingTaxCategories.map(taxCategory => {
      const mapping = mappingsResult.data?.items?.find(
        (m: AnrokTaxCategoryMapping) => m.taxCategoryId === taxCategory.id
      )

      return {
        taxCategoryId: taxCategory.id,
        taxCategoryName: taxCategory.name,
        isDefault: taxCategory.isDefault,
        isMappingDefault: mapping?.isDefault ?? false,
        anrokProductExternalId: mapping?.anrokProductExternalId,
        anrokTaxCategoryId: mapping?.anrokTaxCategoryId,
        anrokTaxCategoryType: mapping?.anrokTaxCategoryType
      }
    })
  }, [allExistingTaxCategories, mappingsResult.data?.items])

  const hasUnmappedCategories = useMemo(
    () => taxCategoriesWithAnrokMappings.some(m => !m.anrokTaxCategoryId),
    [taxCategoriesWithAnrokMappings]
  )

  const hasDefaultMapping = useMemo(
    () => taxCategoriesWithAnrokMappings.some(m => m.isMappingDefault),
    [taxCategoriesWithAnrokMappings]
  )

  const showWarning = hasUnmappedCategories && !hasDefaultMapping

  const createTaxCategoryWithAnrokMapping =
    useCreateTaxCategoryWithAnrokMapping()
  const createAnrokMapping = useCreateAnrokMapping()
  const updateAnrokMapping = useUpdateAnrokMapping()
  const deleteAnrokMapping = useDeleteAnrokMapping()
  const markTaxCategoryAsDefault = useMarkTaxCategoryAsDefault()
  const updateTaxCategory = useUpdateTaxCategory()

  const handleUpdateTaxCategoryName = useCallback(
    async (taxCategoryId: string, name: string) => {
      const taxCategory = allExistingTaxCategories.find(
        tc => tc.id === taxCategoryId
      )
      if (taxCategory) {
        await updateTaxCategory(taxCategory, {
          name,
          description: taxCategory.description
        })
      }
    },
    [allExistingTaxCategories, updateTaxCategory]
  )

  return (
    <SettingsPageLayout
      header="Sales tax"
      subheader="Manage tax categories and their Anrok mappings."
      isError={isError || mappingsResult.isError}
    >
      {showWarning && (
        <Banner variant="warning">
          <Text {...Primary13Regular} color={GreyGrey80}>
            Some tax categories have no Anrok mapping and no default mapping to
            fall back on. Billing may fail for these products.
          </Text>
        </Banner>
      )}

      <SettingsGroup
        isLoading={isLoading || mappingsResult.isLoading}
        id="tax-categories"
        action={
          <Flex
            marginBottom="8px"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text
              as="h3"
              id="tax-categories"
              {...Primary14SemiBold}
              color={GreyGrey80}
            >
              Tax categories
            </Text>

            <AddTaxCategoryForAnrokModal
              onCreate={createTaxCategoryWithAnrokMapping}
            />
          </Flex>
        }
      >
        {taxCategoriesWithAnrokMappings.length === 0 &&
          !isLoading &&
          !mappingsResult.isLoading && (
            <EmptyState
              title="No tax categories"
              icon={IconReceiptTax}
              iconSize="sm"
              body={
                <>
                  You have not added any tax categories yet. Add a tax category,{' '}
                  <Link
                    _hover={{ textDecoration: 'none' }}
                    to="https://docs.sequencehq.com/connectors/anrok"
                    color={IndigoIndigo60}
                    target="_blank"
                  >
                    learn more
                  </Link>
                  .
                </>
              }
            />
          )}

        {taxCategoriesWithAnrokMappings.map(mapping => (
          <TaxCategoryRow
            key={mapping.taxCategoryId}
            mapping={mapping}
            onUpdateName={handleUpdateTaxCategoryName}
            onUpdateMapping={updateAnrokMapping}
            onDeleteMapping={taxCategoryId =>
              void deleteAnrokMapping(taxCategoryId)
            }
            onMarkAsDefault={taxCategoryId =>
              void markTaxCategoryAsDefault(taxCategoryId)
            }
            onAddMapping={async ({
              taxCategoryId,
              taxCategoryName,
              anrokTaxCategoryId,
              anrokTaxCategoryType,
              isDefault
            }) => {
              await createAnrokMapping({
                taxCategoryId,
                taxCategoryName,
                anrokTaxCategoryId,
                anrokTaxCategoryType,
                isDefault
              })
            }}
          />
        ))}
      </SettingsGroup>

      <SettingsGroup title="Tax rates">
        <EmptyState
          title="Managed via Anrok"
          icon={AnrokLogo}
          iconSize="sm"
          body={
            <>
              Tax rates are managed by Anrok,{' '}
              <Link
                _hover={{ textDecoration: 'none' }}
                to="https://docs.sequencehq.com/connectors/anrok"
                color={IndigoIndigo60}
                target="_blank"
              >
                learn more
              </Link>
              .
            </>
          }
        />
      </SettingsGroup>
    </SettingsPageLayout>
  )
}

const AnrokLogo = () => {
  return <SmallIcon url={anrokIntegrationConfig.logoUrl} />
}
